import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAppContext } from '../../../context/AppContext';
import { useWorkspaceContext } from '../providers/WorkspaceProvider';
import { Bot, PenLine, ShieldCheck, GitCompareArrows, LayoutGrid } from 'lucide-react';
import CenterWorkspace from './CenterWorkspace';
import RightWorkspace from './RightWorkspace';

function ResumeSkeleton() {
  return (
    <div 
      className="flex-1 flex h-full min-h-0 overflow-hidden" 
      style={{ background: 'var(--surface-0)', fontFamily: "'Inter', sans-serif" }}
    >
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 0.25; }
        }
        .sk-pulse {
          animation: pulse 1.8s infinite;
          background: var(--divider);
          border-radius: 6px;
        }
      `}</style>
      
      {/* Col 2: Chat panel skeleton */}
      <div className="w-1/2 flex flex-col border-r h-full p-6 space-y-6" style={{ borderColor: 'var(--border-default)' }}>
        <div className="sk-pulse w-32 h-6" />
        <div className="flex-1 space-y-4">
          <div className="sk-pulse w-3/4 h-12 rounded-xl" />
          <div className="sk-pulse w-1/2 h-10 rounded-xl" />
          <div className="sk-pulse w-2/3 h-14 rounded-xl" />
        </div>
        <div className="sk-pulse w-full h-12 rounded-xl" />
      </div>

      {/* Col 3: Canvas skeleton */}
      <div className="w-1/2 flex flex-col items-center justify-center p-8 h-full bg-slate-900/10">
        <div className="sk-pulse w-[400px] h-[550px] rounded-lg shadow-md" />
      </div>
    </div>
  );
}

/**
 * RouteRecovery — 4-layer fallback for invalid/undefined resumeId.
 * Stages: Check → Recover → Open Master → Dashboard fallback
 */
function RouteRecovery({ variants }) {
  const navigate = useNavigate();
  const [stage, setStage] = useState(0);

  const stages = [
    'Checking Workspace...',
    'Recovering Active Resume...',
    'Opening Master Resume...',
  ];

  useEffect(() => {
    // Stage 0 → 1 after 800ms
    const t1 = setTimeout(() => setStage(1), 800);
    // Stage 1 → 2 after another 800ms
    const t2 = setTimeout(() => setStage(2), 1600);
    // Recovery logic: try master → latest variant → dashboard
    const t3 = setTimeout(() => {
      const masterExists = variants.some(v => v.isMaster || v.id === 'master');
      if (masterExists) {
        navigate('/app/resume/master/chat', { replace: true });
        return;
      }
      // Fallback: use the most recently updated variant
      const latest = [...variants].sort(
        (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)
      )[0];
      if (latest?.id) {
        navigate(`/app/resume/${latest.id}/chat`, { replace: true });
        return;
      }
      // Final fallback: dashboard
      navigate('/app/dashboard', { replace: true });
    }, 2400);

    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [navigate, variants]);

  return (
    <div
      className="flex-1 flex flex-col items-center justify-center p-12 text-center"
      style={{ color: 'var(--text-primary)', background: 'var(--surface-0)', fontFamily: "'Inter', sans-serif" }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, maxWidth: 320 }}>
        {/* Spinner */}
        <svg width={44} height={44} viewBox="0 0 44 44" style={{ animation: 'rp-spin 1s linear infinite' }}>
          <circle cx={22} cy={22} r={18} fill="none" stroke="rgba(59,130,246,0.15)" strokeWidth={3} />
          <circle cx={22} cy={22} r={18} fill="none" stroke="#3b82f6" strokeWidth={3}
            strokeLinecap="round" strokeDasharray="28 84" />
        </svg>
        <style>{`@keyframes rp-spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>

        {/* Stage labels */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
          {stages.map((s, i) => (
            <div
              key={s}
              style={{
                fontSize: 13,
                fontWeight: i === stage ? 600 : 400,
                color: i < stage
                  ? '#22c55e'
                  : i === stage
                    ? 'var(--text-primary)'
                    : 'var(--text-secondary)',
                opacity: i > stage ? 0.4 : 1,
                transition: 'all 0.4s ease',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {i < stage && <span style={{ color: '#22c55e' }}>✓</span>}
              {i === stage && <span style={{ color: '#3b82f6', animation: 'rp-spin 1s linear infinite', display: 'inline-block' }}>○</span>}
              {i > stage && <span style={{ color: 'var(--text-secondary)' }}>○</span>}
              {s}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ResumePage({ mode }) {
  const { resumeId } = useParams();
  const location = useLocation();
  const {
    variants = [],
    switchDocument,
    resumeData,
    setEditMode,
    activeVariantId,
    libraryLoaded,
    fetchDocuments,
    activeWorkspace,
    isCreatingDocument,
  } = useAppContext();

  const { activeTab, setActiveTab, theme, setMobileTab } = useWorkspaceContext();

  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Tracks whether we have already attempted a one-shot refresh to resolve a
  // missing document ID. Reset whenever resumeId changes.
  const [refreshAttempted, setRefreshAttempted] = useState(false);
  const refreshAttemptedRef = useRef(false);

  // Reset refresh state when the target document changes
  useEffect(() => {
    refreshAttemptedRef.current = false;
    setRefreshAttempted(false);
  }, [resumeId]);

  // 1. Sync active variant and load data on route param changes
  useEffect(() => {
    // Only attempt switch once library is loaded to ensure we can resolve the
    // correct document type ('resume' or 'variant') from the library.
    if (resumeId && libraryLoaded) {
      const isMaster = resumeId === 'master';
      const expectedActiveId = isMaster ? null : resumeId;
      if (activeVariantId !== expectedActiveId || !resumeData) {
        console.log(`[ResumePage] State mismatch. Route resumeId=${resumeId}, activeVariantId=${expectedActiveId}. Switching document.`);
        // Look up the actual type in the library (could be 'resume' or 'variant')
        const docInLibrary = variants.find(v => String(v.id) === String(resumeId));
        const resolvedType = isMaster ? 'master' : (docInLibrary?.type || 'variant');
        switchDocument({ type: resolvedType, id: resumeId }, false);
      }
    }
  }, [resumeId, activeVariantId, resumeData, switchDocument, variants, libraryLoaded]);

  // 2. Sync editMode with the editor sub-route path
  useEffect(() => {
    if (mode === 'editor' || activeTab === 'editor') {
      setEditMode(true);
    } else {
      setEditMode(false);
    }
  }, [mode, activeTab, setEditMode]);

  // 3. One-shot refresh — triggered when the document ID is not found in the
  //    current library snapshot. This resolves the race condition where the library
  //    is stale but the document exists on the backend.
  //    Only runs for genuinely "unknown" IDs, never for 'master'.
  const hasInvalidId = !resumeId || resumeId === 'undefined' || resumeId === 'null';
  const exists = variants.some(
    item => String(item.id) === String(resumeId) || (resumeId === 'master' && item.isMaster)
  );
  // Navigation originated from a successful Create operation—library was already
  // updated with mergeDocumentMetadata() before navigate() was called.
  const isFromCreate = location.state?.source === 'create';

  useEffect(() => {
    // Only attempt refresh when:
    // - Library is loaded (not during initial fetch)
    // - Document not found in current snapshot
    // - Not a hardcoded invalid ID
    // - Not actively creating (UI guard)
    // - Haven't already tried refreshing for this resumeId
    if (
      libraryLoaded &&
      !hasInvalidId &&
      !exists &&
      !isCreatingDocument &&
      !refreshAttemptedRef.current
    ) {
      refreshAttemptedRef.current = true;
      console.log(`[ResumePage] Document ${resumeId} not in library snapshot. Refreshing library once...`);
      fetchDocuments(activeWorkspace?.id || null).finally(() => {
        setRefreshAttempted(true);
      });
    }
  }, [libraryLoaded, hasInvalidId, exists, isCreatingDocument, resumeId, fetchDocuments, activeWorkspace]);

  // 4. Guard: show skeleton while initial library fetch is in progress
  const isLibraryLoading = !libraryLoaded;
  const isDocumentLoading = exists && !resumeData;

  if (isLibraryLoading || isDocumentLoading) {
    return <ResumeSkeleton />;
  }

  // 5. While a creation is in flight, show skeleton — never RouteRecovery.
  //    isCreatingDocument is UI-only; it is NOT the primary fix (that's mergeDocumentMetadata).
  if (isCreatingDocument) {
    return <ResumeSkeleton />;
  }

  // 6. If document is missing but we haven't yet done the one-shot refresh,
  //    show skeleton while the refresh runs.
  if (!exists && !hasInvalidId && !refreshAttempted) {
    return <ResumeSkeleton />;
  }

  // 7. If this navigation came from a successful Create AND the document still
  //    isn't visible yet (extremely unlikely — mergeDocumentMetadata should have
  //    fixed this), hold the skeleton rather than triggering recovery.
  if (isFromCreate && !exists) {
    return <ResumeSkeleton />;
  }

  // 8. Route Recovery: the document is genuinely missing after a library refresh.
  //    This is the correct path for: stale bookmarks, deleted documents, invalid URLs.
  //    It is NOT reached immediately after a successful creation.
  if (hasInvalidId || (!exists && libraryLoaded && refreshAttempted)) {
    return <RouteRecovery variants={variants} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%', overflow: 'hidden', position: 'relative' }}>
      
      {/* Floating Glassmorphism Centered Navbar */}
      <div style={{
        position: 'absolute',
        top: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        width: isMobile ? 'calc(100% - 24px)' : 'auto',
        justifyContent: isMobile ? 'space-between' : 'center',
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? 1 : 2,
        padding: isMobile ? '8px 10px' : '6px 14px',
        borderRadius: 24,
        background: theme === 'dark'
          ? 'rgba(15, 15, 22, 0.72)'
          : 'rgba(255, 255, 255, 0.72)',
        border: theme === 'dark'
          ? '1px solid rgba(255,255,255,0.07)'
          : '1px solid rgba(0,0,0,0.08)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        boxShadow: theme === 'dark'
          ? '0 4px 24px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.05) inset'
          : '0 4px 20px rgba(0,0,0,0.1), 0 1px 0 rgba(255,255,255,0.8) inset',
      }}>
        {[
          { id: 'chat',      label: 'AI Builder',icon: Bot },
          { id: 'editor',    label: 'Editor',    icon: PenLine },
          { id: 'ats',       label: 'ATS Analysis', icon: ShieldCheck },
          { id: 'jd',        label: 'JD Match',  icon: GitCompareArrows },
          { id: 'templates', label: 'Templates', icon: LayoutGrid },
        ].map((tab) => {
          const active = activeTab === tab.id || (tab.id === 'chat' && activeTab === 'ai') || (tab.id === 'jd' && activeTab === 'jd_match');
          const IconComp = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                if (typeof setMobileTab === 'function') {
                  setMobileTab(1);
                }
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobile ? 2 : 3,
                padding: isMobile ? '5px 3px' : '7px 13px',
                borderRadius: 14,
                border: 'none',
                cursor: 'pointer',
                outline: 'none',
                background: 'transparent',
                color: active
                  ? 'var(--brand)'
                  : (theme === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.4)'),
                transition: 'all 150ms ease',
                minWidth: isMobile ? 'auto' : 60,
              }}
              onMouseEnter={e => {
                if (!active) {
                  e.currentTarget.style.color = theme === 'dark' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)';
                }
              }}
              onMouseLeave={e => {
                if (!active) {
                  e.currentTarget.style.color = theme === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.4)';
                }
              }}
            >
              <IconComp size={isMobile ? 11 : 15} strokeWidth={active ? 2.2 : 1.8} />
              <span style={{ 
                fontSize: isMobile ? 7.5 : 9.5, 
                fontWeight: active ? 700 : 500, 
                letterSpacing: '0.02em', 
                lineHeight: 1,
                whiteSpace: 'nowrap'
              }}>
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>



      {/* Main workspace panels */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <CenterWorkspace />
        <RightWorkspace />
      </div>
    </div>
  );
}
